import _extends from "@babel/runtime/helpers/extends";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import React from 'react';
import PropTypes from 'prop-types';
import cuid from 'cuid';
import styled, { css } from 'styled-components';
import { calculateRem } from '@vds-core/utilities';
import { AccessibilityTokens } from '@vds-tokens/accessibility';
import { LayoutTokens } from '@vds-tokens/layout';
import { ColorTokens } from '@vds-tokens/color';
import { TextLink } from '@vds-core/buttons';
import { Checkbox } from '@vds-core/checkboxes';
import { Line } from '@vds-core/lines';
import { Toggle } from '@vds-core/toggles';
import { Body, Fonts } from '@vds-core/typography';
import { TypographyTokens } from '@vds-tokens/typography';
import { Icon, RESTRICTED_Icon } from '@vds-core/icons';
var paddingMobile = calculateRem(LayoutTokens.space['6X'].value);
var paddingNonMobile = calculateRem(LayoutTokens.space['8X'].value);
var actionElemPaddingMobile = calculateRem(LayoutTokens.space['3X'].value);
var actionElemPaddingNonMobile = calculateRem(LayoutTokens.space['4X'].value);
var leftElemPaddingMobile = calculateRem(LayoutTokens.space['4X'].value);
var leftElemPaddingNonMobile = calculateRem(LayoutTokens.space['6X'].value);
var transitionDuration = '350ms';
var transitionTimingFunction = 'cubic-bezier(0.22, 0.61, 0.36, 1.0)';
var hoverTransitionIconDistance = LayoutTokens.space['1X'].value;
/**
 * Helper function to manipulate hover & active
 * state of text link when hovering or clicking
 * the overall list item
 */

var _getTextLinkStyles = function _getTextLinkStyles(surface) {
  var color = {
    hover: ColorTokens.elements.primary["on".concat(surface)].value,
    active: ColorTokens.interactive.active["on".concat(surface)].value
  };
  return color;
};

var propTypes = {
  /**
   * If true, list group item will render custom children passed in and
   * will not render actionElement prop
   */
  custom: PropTypes.bool,

  /**
   * If provided, will render the ListGroupItem when surface is light or dark.
   */
  surface: PropTypes.oneOf(['light', 'dark']),

  /**
   * If true, will disable user from clicking the list group item
   */
  disabled: PropTypes.bool,

  /**
   * @ignore
   * If true, can only be used to display on first list group item in ListGroup component
   */
  topLine: PropTypes.bool,

  /**
   * @ignore
   * If true, can only be used to display on the last list group item in ListGroup component
   */
  bottomLine: PropTypes.bool,

  /**
   * If provided, hides aria attributes from a screen reader.
   */
  ariaHidden: PropTypes.string,

  /**
   * If provided, used as string that labels the element for accessibility.
   */
  ariaLabel: PropTypes.string,

  /**
   * If provided, used as string that defines the element's aria role for accessibility.
   */
  ariaRole: PropTypes.string,

  /**
   * If provided, list group item renders subtitle on the right before icon
   * or when actionElement is 'none'
   */
  subtitleRight: PropTypes.string,

  /**
   * Options to render actionElement. Defaults to icon
   */
  actionElement: PropTypes.oneOf(['none', 'icon', 'checkbox', 'textLink', 'toggle']),

  /**
   * Config object for toggle when actionElement is toggle
   */
  toggle: PropTypes.shape({
    on: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    id: PropTypes.string,
    inputId: PropTypes.string
  }),

  /**
   * Determines whether mobile, or desktop viewports are used.
   */
  viewport: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),

  /**
   * Config object for textLink when actionElement is textLink
   */
  textLink: PropTypes.shape({
    text: PropTypes.string
  }),

  /**
   * Config object for descriptiveIcon. Can be used for all actionElement options
   */
  descriptiveIcon: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.oneOf(['XSmall', 'small', 'medium', 'large', 'XLarge']), PropTypes.string]),
    color: PropTypes.string
  }),

  /**
   * Function fired when list item is clicked
   * @note Providing the onClick prop will render the content as a link
   */
  onClick: PropTypes.func,

  /**
   * String, React Component, or HTML to render content of ListItem
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]),

  /**
   * Allows a unique id to be passed to the component and applied to the outermost wrapper of list item.
   */
  id: PropTypes.string,

  /**
   * If provided, will determine the prominence of the title and subtitle positions in the list item
   */
  textHierarchy: PropTypes.oneOf(['titleProminent', 'subtitleProminent']),

  /**
   * Allows a string to be provided for analytics.
   */
  'data-analyticstrack': PropTypes.string,

  /**
   * Allows a string to be provided for analytics.
   */
  'data-track': PropTypes.string,

  /**
   * Allows a string to be provided for ignoring analytics tagging.
   */
  'data-track-ignore': PropTypes.string,

  /**
   * Allows a string to be provided for click stream.
   */
  'data-clickstream': PropTypes.string
};
var defaultProps = {
  actionElement: 'icon',
  custom: false,
  surface: 'light',
  disabled: false,
  topLine: false,
  bottomLine: false,
  textHierarchy: 'titleProminent',
  onClick: function onClick() {}
};
var ComponentWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ComponentWrapper",
  componentId: "VDS__sc-5z0rh5-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  width: 100%;\n"])));
var HiddenLabel = /*#__PURE__*/styled.label.withConfig({
  displayName: "HiddenLabel",
  componentId: "VDS__sc-5z0rh5-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  margin: -1px;\n  overflow: hidden;\n  clip: rect(0, 0, 0, 0);\n  white-space: nowrap;\n  border: 0;\n  visibility: hidden;\n"])));
var StyledListItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledListItem",
  componentId: "VDS__sc-5z0rh5-2"
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  position: relative;\n  outline: none;\n  list-style: none;\n  padding-bottom: ", ";\n  padding-top: ", ";\n  width: 100%;\n\n  ", ";\n\n  ", ";\n"])), function (_ref) {
  var paddingBottom = _ref.paddingBottom;
  return paddingBottom;
}, function (_ref2) {
  var paddingTop = _ref2.paddingTop;
  return paddingTop;
}, function (_ref3) {
  var surface = _ref3.surface,
      actionElement = _ref3.actionElement;
  return actionElement === 'none' && css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      &:focus:not(:hover) {\n        &::before {\n          border: ", "\n            ", "\n            ", ";\n          border-radius: ", ";\n          content: '';\n          position: absolute;\n          pointer-events: none;\n          top: 50%;\n          left: 50%;\n          transform: translate(-50%, -50%);\n          width: calc(100% + 6px);\n          height: calc(100% + 6px);\n        }\n      }\n    "])), AccessibilityTokens.focusring.borderwidth.value, AccessibilityTokens.focusring.borderstyle.value, AccessibilityTokens.color.focusring["on".concat(surface)].value, calculateRem(2));
}, function (_ref4) {
  var disabled = _ref4.disabled,
      surface = _ref4.surface,
      actionElement = _ref4.actionElement;
  return !disabled && css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      &:hover {\n        cursor: pointer;\n        ", "\n      }\n      &:active {\n        ", ";\n        ", ";\n      }\n    "])), actionElement === 'icon' && "\n          .iconContainer {\n              transform: translateX(".concat(hoverTransitionIconDistance, ");\n              transition: transform ").concat(transitionDuration, "\n                ").concat(transitionTimingFunction, ";\n          \n          }\n          "), actionElement === 'icon' && "\n          .iconContainer {\n            svg {\n              path {\n                fill: ".concat(ColorTokens.interactive.active["on".concat(surface)].value, ";\n              }\n            }\n          }\n          "), actionElement === 'textLink' && "\n          a {\n            color: ".concat(_getTextLinkStyles(surface).active, ";\n            border-bottom-color: ").concat(_getTextLinkStyles(surface).active, ";\n          }\n          "));
});
var ChildWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ChildWrapper",
  componentId: "VDS__sc-5z0rh5-3"
})(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  ", "\n"])), function (_ref5) {
  var custom = _ref5.custom,
      viewport = _ref5.viewport;
  return custom && css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n      font-family: ", ";\n      font-size: ", ";\n      line-height: ", ";\n    "])), Fonts.VerizonNHGeDS, viewport === 'mobile' ? TypographyTokens.fontsize.title[16].value : TypographyTokens.fontsize.title[20].value, viewport === 'mobile' ? TypographyTokens.lineheight.title[20].value : TypographyTokens.lineheight.title[24].value);
});
var TextContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "TextContainer",
  componentId: "VDS__sc-5z0rh5-4"
})(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n  display: flex;\n  flex: 1;\n  flex-direction: column;\n  justify-content: center;\n  width: 100%;\n  overflow-wrap: break-word;\n  word-break: break-word;\n  ", "\n"])), function (_ref6) {
  var hasMarginLeft = _ref6.hasMarginLeft,
      isMobile = _ref6.isMobile;
  return hasMarginLeft && "margin-left: ".concat(isMobile ? leftElemPaddingMobile : leftElemPaddingNonMobile, ";");
});
var SubtitleRightWithIconContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "SubtitleRightWithIconContainer",
  componentId: "VDS__sc-5z0rh5-5"
})(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  text-align: end;\n  max-width: ", ";\n  padding-left: ", ";\n  overflow-wrap: break-word;\n  word-break: break-word;\n"])), function (_ref7) {
  var isMobile = _ref7.isMobile;
  return isMobile ? '50%' : '25%';
}, function (_ref8) {
  var isMobile = _ref8.isMobile;
  return isMobile ? actionElemPaddingMobile : actionElemPaddingNonMobile;
});
/**
 * Must set span wrapper for text link's children to inline-block
 * and vertical-align to text-top to achieve overflow ellipsis
 */

var TextLinkContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TextLinkContainer",
  componentId: "VDS__sc-5z0rh5-6"
})(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n  max-width: ", ";\n  padding-left: ", ";\n  span:last-child {\n    height: 100%;\n    display: inline-block;\n    white-space: nowrap;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    vertical-align: text-top;\n  }\n"])), function (_ref9) {
  var isMobile = _ref9.isMobile;
  return isMobile ? '50%' : '25%';
}, function (_ref10) {
  var isMobile = _ref10.isMobile;
  return isMobile ? actionElemPaddingMobile : actionElemPaddingNonMobile;
});
var ToggleContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "ToggleContainer",
  componentId: "VDS__sc-5z0rh5-7"
})(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n  display: flex;\n  margin-left: ", ";\n"])), function (_ref11) {
  var isMobile = _ref11.isMobile;
  return isMobile ? actionElemPaddingMobile : actionElemPaddingNonMobile;
});
var SubtitleContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "SubtitleContainer",
  componentId: "VDS__sc-5z0rh5-8"
})(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref12) {
  var paddingPosition = _ref12.paddingPosition;
  return "\n    padding-".concat(paddingPosition, ": ").concat(calculateRem(LayoutTokens.space['1X'].value), ";\n  ");
});
var IconSectionWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "IconSectionWrapper",
  componentId: "VDS__sc-5z0rh5-9"
})(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: center;\n  outline: none;\n  list-style: none;\n  .iconContainer {\n    outline: none;\n    transform: translateX(-", ");\n    transition: transform ", " ", ";\n  }\n  ", "\n  ", "\n"])), hoverTransitionIconDistance, transitionDuration, transitionTimingFunction, function (_ref13) {
  var surface = _ref13.surface;
  return css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    &.active {\n      svg {\n        path {\n          fill: ", ";\n        }\n      }\n    }\n  "])), ColorTokens.interactive.active["on".concat(surface)].value);
}, function (_ref14) {
  var paddingLeft = _ref14.paddingLeft;
  return paddingLeft && " padding-left: ".concat(paddingLeft, "; ");
});
var IconContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "IconContainer",
  componentId: "VDS__sc-5z0rh5-10"
})(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n  display: flex;\n  position: relative;\n  outline: none;\n  &:focus:not(:hover) {\n    &:before {\n      border: 1px dashed\n        ", ";\n      border-radius: 2px;\n      content: '';\n      height: calc(100% + ", ");\n      left: 50%;\n      position: absolute;\n      top: 50%;\n      transform: translate(-50%, -50%);\n      width: calc(100% + ", ");\n    }\n  }\n"])), function (_ref15) {
  var surface = _ref15.surface;
  return AccessibilityTokens.color.focusring["on".concat(surface)].value;
}, AccessibilityTokens.focusring.space.offset.value, AccessibilityTokens.focusring.space.offset.value);
var DescriptiveIconContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "DescriptiveIconContainer",
  componentId: "VDS__sc-5z0rh5-11"
})(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: center;\n  margin-right: ", ";\n"])), function (_ref16) {
  var isMobile = _ref16.isMobile;
  return isMobile ? leftElemPaddingMobile : leftElemPaddingNonMobile;
});
var CheckboxWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "CheckboxWrapper",
  componentId: "VDS__sc-5z0rh5-12"
})(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n  display: flex;\n  margin-right: ", ";\n"])), function (_ref17) {
  var isMobile = _ref17.isMobile;
  return isMobile ? leftElemPaddingMobile : leftElemPaddingNonMobile;
});

var ListGroupItem = /*#__PURE__*/function (_React$Component) {
  _inherits(ListGroupItem, _React$Component);

  var _super = _createSuper(ListGroupItem);

  function ListGroupItem(props) {
    var _this;

    _classCallCheck(this, ListGroupItem);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "_getTitleAndSubTitle", function () {
      var children = _this.props.children;
      if (!children) return;

      var getChildren = function getChildren(child) {
        var childType = _this._getChildType(child);

        if (childType === 'ListGroupItemTitle') {
          _this.setState({
            listItemTitle: child.props.children
          });
        } else if (childType === 'ListGroupItemSubtitle') {
          _this.setState({
            listItemSubtitle: child.props.children
          });
        }
      };

      if (children.length > 1) {
        children.map(function (child) {
          if (child && child.props && child.props.children) {
            getChildren(child);
          }
        });
      } else {
        getChildren(children);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "_getChildType", function (child) {
      return child && /*#__PURE__*/React.isValidElement(child) && child.type && child.type.displayName ? child.type.displayName : '';
    });

    _defineProperty(_assertThisInitialized(_this), "_onActionElementClick", function () {
      _this.setState({
        selected: !_this.state.selected
      });
    });

    _defineProperty(_assertThisInitialized(_this), "_handleOnClick", function (e) {
      // to handle change events from checkbox as they only have 1 call back for both events
      if (e.type === 'keydown') return _this._handleOnKeyDown(e); //

      var _this$props = _this.props,
          onClick = _this$props.onClick,
          actionElement = _this$props.actionElement;
      var hasInput = actionElement === 'toggle' || actionElement === 'checkbox';
      var hasToggle = actionElement === 'toggle';
      if (hasInput) _this._onActionElementClick();

      if (onClick) {
        hasToggle && e.preventDefault();
        onClick(e, hasInput && {
          name: _this.state.listItemTitle,
          selected: !_this.state.selected,
          id: _this.actionElementInputId
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "_handleOnKeyDown", function (e) {
      var _this$props2 = _this.props,
          onClick = _this$props2.onClick,
          actionElement = _this$props2.actionElement;
      var keyCode = e.keyCode;
      var space = keyCode === 32;
      var enter = keyCode === 13;
      var hasInput = actionElement === 'toggle' || actionElement === 'checkbox';
      if (space) e.preventDefault();
      if (hasInput) _this._onActionElementClick();
      if (enter || space && onClick) onClick(e, hasInput && {
        name: _this.state.listItemTitle,
        selected: !_this.state.selected,
        id: _this.actionElementInputId
      });
    });

    _defineProperty(_assertThisInitialized(_this), "_renderLine", function () {
      var surface = _this.props.surface;
      return /*#__PURE__*/React.createElement(Line, {
        type: "secondary",
        surface: surface
      });
    });

    _defineProperty(_assertThisInitialized(_this), "_renderWithIcon", function () {
      var _this$props3 = _this.props,
          surface = _this$props3.surface,
          viewport = _this$props3.viewport,
          subtitleRight = _this$props3.subtitleRight,
          disabled = _this$props3.disabled,
          ariaRole = _this$props3.ariaRole,
          ariaHidden = _this$props3.ariaHidden,
          onClick = _this$props3.onClick;
      var isMobile = viewport === 'mobile';
      var paddingLeft = isMobile ? actionElemPaddingMobile : actionElemPaddingNonMobile;
      return /*#__PURE__*/React.createElement(IconSectionWrapper, {
        className: "actionIconSpan",
        id: "actionIconSpan",
        paddingLeft: subtitleRight && paddingLeft,
        surface: surface
      }, /*#__PURE__*/React.createElement(IconContainer, {
        surface: surface,
        onKeyDown: _this._handleOnKeyDown,
        tabIndex: !disabled ? 0 : -1,
        className: "iconContainer",
        id: _this.actionElementInputId,
        "aria-hidden": ariaHidden,
        "aria-label": disabled ? undefined : _this._calculateAriaLabel(),
        role: disabled ? undefined : ariaRole ? ariaRole : onClick ? 'link' : 'button'
      }, /*#__PURE__*/React.createElement(RESTRICTED_Icon, {
        ariaHidden: true,
        surface: surface,
        bold: false,
        name: "pagination-right-arrow",
        tabIndex: -1,
        size: isMobile ? 'small' : 'medium'
      })));
    });

    _defineProperty(_assertThisInitialized(_this), "_renderWithSubtitleRight", function () {
      var _this$props4 = _this.props,
          subtitleRight = _this$props4.subtitleRight,
          viewport = _this$props4.viewport,
          surface = _this$props4.surface;
      var isMobile = viewport === 'mobile';
      var subtitleRightColor = ColorTokens.elements.primary["on".concat(surface)].value;
      return /*#__PURE__*/React.createElement(Body, {
        color: subtitleRightColor,
        size: isMobile ? 'small' : 'large',
        viewport: viewport
      }, subtitleRight);
    });

    _defineProperty(_assertThisInitialized(_this), "_renderWithTextLink", function () {
      var _this$props5 = _this.props,
          surface = _this$props5.surface,
          disabled = _this$props5.disabled,
          viewport = _this$props5.viewport,
          textLink = _this$props5.textLink;
      var isMobile = viewport === 'mobile';
      if (!textLink || !textLink.text) return null;
      return /*#__PURE__*/React.createElement(TextLinkContainer, {
        isMobile: isMobile
      }, /*#__PURE__*/React.createElement(TextLink, {
        type: "standAlone",
        size: isMobile ? 'small' : 'large',
        surface: surface,
        disabled: disabled,
        viewport: viewport,
        onClick: _this._handleOnClick,
        ariaLabel: disabled ? undefined : _this._calculateAriaLabel(),
        onKeyDown: function onKeyDown(e) {
          if (e.keyCode === 32) {
            e.preventDefault();
          }
        }
      }, textLink.text));
    });

    _defineProperty(_assertThisInitialized(_this), "_renderWithToggle", function () {
      var _this$props6 = _this.props,
          surface = _this$props6.surface,
          disabled = _this$props6.disabled,
          viewport = _this$props6.viewport,
          toggle = _this$props6.toggle;
      var isMobile = viewport === 'mobile';
      return /*#__PURE__*/React.createElement(ToggleContainer, {
        isMobile: isMobile
      }, /*#__PURE__*/React.createElement(Toggle, {
        on: _this.state.selected,
        showText: false,
        disabled: disabled,
        tabIndex: 0,
        value: toggle && toggle.value ? toggle.value : _this.state.listItemTitle,
        name: toggle && toggle.name ? toggle.name : _this.state.listItemTitle,
        inputId: toggle && toggle.inputId ? toggle.inputId : _this.actionElementInputId,
        onChange: _this._handleOnClick,
        surface: surface
      }));
    });

    _defineProperty(_assertThisInitialized(_this), "_renderWithDescriptiveIcon", function () {
      var _this$props7 = _this.props,
          surface = _this$props7.surface,
          viewport = _this$props7.viewport,
          descriptiveIcon = _this$props7.descriptiveIcon;
      var isMobile = viewport === 'mobile';

      var _getDescriptiveIconSize = function _getDescriptiveIconSize() {
        if (descriptiveIcon && descriptiveIcon.size) return descriptiveIcon.size;
        return isMobile ? 'large' : 'extraLarge';
      };

      if (!descriptiveIcon || !Object.keys(descriptiveIcon).length || !descriptiveIcon.name) return null;
      return /*#__PURE__*/React.createElement(DescriptiveIconContainer, {
        isMobile: isMobile
      }, /*#__PURE__*/React.createElement(Icon, {
        surface: surface,
        tabIndex: -1,
        size: _getDescriptiveIconSize(),
        name: descriptiveIcon.name,
        color: descriptiveIcon.color
      }));
    });

    _defineProperty(_assertThisInitialized(_this), "_renderWithCheckbox", function () {
      var _this$props8 = _this.props,
          viewport = _this$props8.viewport,
          surface = _this$props8.surface,
          disabled = _this$props8.disabled;
      var isMobile = viewport === 'mobile';
      return /*#__PURE__*/React.createElement(CheckboxWrapper, {
        isMobile: isMobile
      }, /*#__PURE__*/React.createElement(HiddenLabel, {
        htmlFor: _this.actionElementInputId
      }), /*#__PURE__*/React.createElement(Checkbox, {
        customLabel: true,
        surface: surface,
        disabled: disabled,
        name: _this.state.listItemTitle,
        value: _this.state.listItemTitle,
        inputId: _this.actionElementInputId,
        selected: _this.state.selected,
        onChange: _this._handleOnClick,
        ariaLabel: _this._calculateAriaLabel()
      }));
    });

    _defineProperty(_assertThisInitialized(_this), "_renderRightSideElements", function () {
      var _this$props9 = _this.props,
          actionElement = _this$props9.actionElement,
          subtitleRight = _this$props9.subtitleRight,
          viewport = _this$props9.viewport;
      var isMobile = viewport === 'mobile';
      var hasIcon = actionElement === 'icon';

      switch (actionElement) {
        case 'textLink':
          return _this._renderWithTextLink();

        case 'toggle':
          return _this._renderWithToggle();

        case 'none':
          return !subtitleRight ? null : /*#__PURE__*/React.createElement(SubtitleRightWithIconContainer, {
            isMobile: isMobile
          }, _this._renderWithSubtitleRight());

        case 'checkbox':
          return null;

        case 'icon':
        default:
          return /*#__PURE__*/React.createElement(SubtitleRightWithIconContainer, {
            isMobile: isMobile
          }, !!subtitleRight && _this._renderWithSubtitleRight(), hasIcon && _this._renderWithIcon());
      }
    });

    _defineProperty(_assertThisInitialized(_this), "_renderLeftSideElements", function () {
      var actionElement = _this.props.actionElement;
      return /*#__PURE__*/React.createElement(React.Fragment, null, actionElement === 'checkbox' && _this._renderWithCheckbox(), _this._renderWithDescriptiveIcon());
    });

    _defineProperty(_assertThisInitialized(_this), "_renderChildrenSlots", function () {
      var _this$props10 = _this.props,
          children = _this$props10.children,
          surface = _this$props10.surface,
          viewport = _this$props10.viewport,
          textHierarchy = _this$props10.textHierarchy,
          actionElement = _this$props10.actionElement;
      var isMobile = viewport === 'mobile';
      var Title, Subtitle, ImageItem;
      React.Children.forEach(children, function (child, index) {
        var childType = _this._getChildType(child);

        if (childType === 'ListGroupItemTitle') {
          Title = /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, child.props), {}, {
            viewport: viewport,
            surface: surface
          }));
        } else if (childType === 'ListGroupItemSubtitle') {
          Subtitle = /*#__PURE__*/React.createElement(SubtitleContainer, {
            paddingPosition: textHierarchy === 'titleProminent' ? 'top' : 'bottom'
          }, /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, child.props), {}, {
            viewport: viewport,
            surface: surface
          })));
        } else if (childType === 'ListGroupItemImage') {
          ImageItem = child;
        }
      });

      var _renderTextHierarchy = function _renderTextHierarchy() {
        if (!!Title && !!Subtitle) {
          return textHierarchy === 'titleProminent' ? /*#__PURE__*/React.createElement(React.Fragment, null, Title, Subtitle) : /*#__PURE__*/React.createElement(React.Fragment, null, Subtitle, Title);
        } else if (!!Title && !Subtitle) {
          return Title;
        } else if (!!Subtitle && !Title) {
          return Subtitle;
        }
      };

      return /*#__PURE__*/React.createElement(React.Fragment, null, !!ImageItem && ImageItem, /*#__PURE__*/React.createElement(TextContainer, {
        hasMarginLeft: !!ImageItem,
        isMobile: isMobile
      }, _renderTextHierarchy()));
    });

    _defineProperty(_assertThisInitialized(_this), "_calculateAriaLabel", function () {
      var _this$props11 = _this.props,
          ariaLabel = _this$props11.ariaLabel,
          subtitleRight = _this$props11.subtitleRight,
          textLink = _this$props11.textLink,
          textHierarchy = _this$props11.textHierarchy;

      var getByTextHierarchy = function getByTextHierarchy() {
        return textHierarchy === 'subtitleProminent' && _this.state.listItemSubtitle ? _this.state.listItemSubtitle : _this.state.listItemTitle;
      };

      if (ariaLabel) return ariaLabel;else if (subtitleRight) return "".concat(_this.state.listItemTitle, " ").concat(_this.state.listItemSubtitle, " ").concat(subtitleRight);else if (textLink) return "".concat(textLink.text, " ").concat(getByTextHierarchy());else return "".concat(getByTextHierarchy());
    });

    _this.actionElementInputId = "".concat(cuid());
    _this.listItemId = "".concat(cuid());
    _this.state = {
      selected: false,
      listItemTitle: '',
      listItemSubtitle: ''
    };
    return _this;
  }

  _createClass(ListGroupItem, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this._getTitleAndSubTitle();

      var _this$props12 = this.props,
          actionElement = _this$props12.actionElement,
          toggle = _this$props12.toggle;
      actionElement === 'toggle' && toggle && toggle.on && this.setState({
        selected: true
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props13 = this.props,
          surface = _this$props13.surface,
          disabled = _this$props13.disabled,
          topLine = _this$props13.topLine,
          bottomLine = _this$props13.bottomLine,
          viewport = _this$props13.viewport,
          children = _this$props13.children,
          custom = _this$props13.custom,
          actionElement = _this$props13.actionElement,
          onClick = _this$props13.onClick,
          id = _this$props13.id,
          analyticsTrack = _this$props13['data-analyticstrack'],
          track = _this$props13['data-track'],
          ignoreTrack = _this$props13['data-track-ignore'],
          clickStream = _this$props13['data-clickstream'];
      var isMobile = viewport === 'mobile';
      var role = onClick ? 'link' : '';
      return /*#__PURE__*/React.createElement(ComponentWrapper, {
        id: id
      }, topLine && this._renderLine(), /*#__PURE__*/React.createElement(StyledListItem, _extends({
        id: this.listItemId,
        "aria-label": actionElement === 'none' && this._calculateAriaLabel(),
        actionElement: actionElement,
        disabled: disabled,
        surface: surface,
        tabIndex: disabled || actionElement !== 'none' ? -1 : 0 // Only allow to tab to the entire list item if actionElement is 'none'
        ,
        paddingBottom: isMobile ? paddingMobile : paddingNonMobile,
        paddingTop: isMobile ? paddingMobile : paddingNonMobile,
        onClick: !disabled ? this._handleOnClick : undefined,
        "data-analyticstrack": analyticsTrack,
        "data-track": track,
        "data-track-ignore": ignoreTrack,
        "data-clickstream": clickStream
      }, actionElement === 'none' && {
        onKeyDown: this._handleOnKeyDown
      }), /*#__PURE__*/React.createElement(ChildWrapper, {
        custom: custom,
        viewport: viewport
      }, !custom && this._renderLeftSideElements(), !custom && this._renderChildrenSlots(), custom && React.Children.map(children, function (child, index) {
        var childType = _this2._getChildType(child);

        var isCustom = childType !== 'ListGroupItemImage' && childType !== 'ListGroupItemTitle' && childType !== 'ListGroupItemSubtitle';
        if (!childType || isCustom) return child;
      }), !custom && this._renderRightSideElements())), bottomLine && this._renderLine());
    }
  }]);

  return ListGroupItem;
}(React.Component);

_defineProperty(ListGroupItem, "componentType", 'ListGroupItem');

ListGroupItem.propTypes = propTypes;
ListGroupItem.defaultProps = defaultProps;
export default ListGroupItem;